import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { FaGoogle, FaGithub, FaApple } from 'react-icons/fa';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import NotificationBanner from '../../components/ui/NotificationBanner';
import { useAuth } from '../../contexts/AuthContext';
import ApiService from '../../services/apiService';

const Register = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        agreeToTerms: false
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const { signup, signInWithGoogle, signInWithGithub, signInWithApple } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error when user starts typing
        if (error) setError('');
    };

    const validateForm = () => {
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match.');
            return false;
        }
        
        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long.');
            return false;
        }
        
        if (!formData.agreeToTerms) {
            setError('You must agree to the Terms of Service and Privacy Policy.');
            return false;
        }
        
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (!validateForm()) {
            return;
        }
        
        setIsSubmitting(true);

        try {
            // Create account - user stays signed in
            const userCredential = await signup(formData.email, formData.password, formData.name);
            
            // Save user to backend database
            const token = await userCredential.user.getIdToken();
            await ApiService.saveUser(userCredential.user, token);
            
            // Redirect to email verification page (user stays logged in)
            navigate('/auth/verify-email');
        } catch (error) {
            console.error('Registration error:', error);
            switch (error.code) {
                case 'auth/email-already-in-use':
                    setError('An account with this email address already exists.');
                    break;
                case 'auth/invalid-email':
                    setError('Please enter a valid email address.');
                    break;
                case 'auth/weak-password':
                    setError('Password is too weak. Please choose a stronger password.');
                    break;
                case 'auth/operation-not-allowed':
                    setError('Email/password accounts are not enabled. Please contact support.');
                    break;
                default:
                    setError('Failed to create account. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSocialSignup = async (provider) => {
        setError('');
        setLoading(true);

        try {
            let result;
            switch (provider) {
                case 'google':
                    result = await signInWithGoogle();
                    break;
                case 'github':
                    result = await signInWithGithub();
                    break;
                case 'apple':
                    result = await signInWithApple();
                    break;
                default:
                    throw new Error('Unknown provider');
            }
            
            if (result.user) {
                // Save user to backend database
                const token = await result.user.getIdToken();
                await ApiService.saveUser(result.user, token);
                
                // Social logins are auto-verified, redirect to dashboard
                navigate('/dashboard');
            }
        } catch (error) {
            console.error(`${provider} signup error:`, error);
            switch (error.code) {
                case 'auth/popup-closed-by-user':
                    setError('Registration was cancelled.');
                    break;
                case 'auth/popup-blocked':
                    setError('Registration popup was blocked. Please allow popups for this site.');
                    break;
                case 'auth/account-exists-with-different-credential':
                    setError('An account already exists with the same email address but different sign-in credentials.');
                    break;
                case 'auth/operation-not-allowed':
                    setError(`${provider} sign-in is not enabled. Please contact support.`);
                    break;
                default:
                    setError(`Failed to sign up with ${provider}. Please try again.`);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Register - Nexgen Learning - AI-Driven Education</title>
                <meta name="description" content="Create your Nexgen Learning account to start your AI-driven education journey." />
            </Helmet>
            <Header />
            <NotificationBanner />
            <main className="min-h-screen bg-gray-50 pt-28 pb-20">
                <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-gray-900">Create Account</h1>
                        <p className="mt-2 text-gray-600">Join our learning community</p>
                    </div>

                    <div className="bg-white rounded-lg shadow-lg p-8">
                        {error && (
                            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}
                        
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                    Full Name
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                                    placeholder="John Doe"
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                                    placeholder="your.email@example.com"
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                    Password
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                                    placeholder="••••••••"
                                    disabled={isSubmitting}
                                />
                                <p className="mt-1 text-xs text-gray-500">Password must be at least 6 characters long</p>
                            </div>

                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                    Confirm Password
                                </label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                                    placeholder="••••••••"
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="agreeToTerms"
                                    name="agreeToTerms"
                                    checked={formData.agreeToTerms}
                                    onChange={handleChange}
                                    required
                                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                    disabled={isSubmitting}
                                />
                                <label htmlFor="agreeToTerms" className="ml-2 block text-sm text-gray-700">
                                    I agree to the{' '}
                                    <Link to="/terms" className="text-primary hover:text-primary-dark">
                                        Terms of Service
                                    </Link>{' '}
                                    and{' '}
                                    <Link to="/privacy" className="text-primary hover:text-primary-dark">
                                        Privacy Policy
                                    </Link>
                                </label>
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-primary text-white py-3 px-6 rounded-md font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? 'Creating Account...' : 'Create Account'}
                            </button>
                        </form>

                        <div className="mt-6">
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-300"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-2 bg-white text-gray-500">Or sign up with</span>
                                </div>
                            </div>

                            <div className="mt-6 grid grid-cols-3 gap-3">
                                <button
                                    onClick={() => handleSocialSignup('google')}
                                    disabled={loading}
                                    className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <FaGoogle className="h-5 w-5 text-red-600" />
                                </button>
                                <button
                                    onClick={() => handleSocialSignup('github')}
                                    disabled={loading}
                                    className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <FaGithub className="h-5 w-5 text-gray-900" />
                                </button>
                                <button
                                    onClick={() => handleSocialSignup('apple')}
                                    disabled={loading}
                                    className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <FaApple className="h-5 w-5 text-gray-900" />
                                </button>
                            </div>
                        </div>

                        <p className="mt-6 text-center text-sm text-gray-600">
                            Already have an account?{' '}
                            <Link to="/auth/login" className="text-primary hover:text-primary-dark">
                                Sign in
                            </Link>
                        </p>
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
};

export default Register; 